import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  getIdTokenResult,
  signOut,
} from "firebase/auth";
import {
  doc,
  getDoc,
  collection,
  collectionGroup,
  query,
  where,
  limit,
  getDocs,
} from "firebase/firestore";
import { getAuthClient, ensureSessionPersistence, repairFirebaseAuthStorage, isFirebaseAuthStorageError, db } from "./firebaseConfig";
import { getLandingPath } from "./auth/landing";
import { hasLeagueAccess, normalizeApps } from "./auth/appAccess";
import { hasOnTimeAccess } from "./ontime/utils/access";
import { clearOnTimeSession } from "./ontime/auth/session";

const APP_STORAGE_KEY = "im.loginApp";
const LOGIN_IN_PROGRESS_KEY = "im.loginInProgress";
const USERNAME_OPTIONS_KEY = "matrixos.loginUsernames";

function normalizeAppSelection(value) {
  const v = String(value || "").trim().toLowerCase();
  if (["mc", "morpheus", "morpheus-count", "morphius"].includes(v)) return "mc";
  if (["ontime", "on_time", "on-time"].includes(v)) return "ontime";
  if (["league", "matrix-league", "matrix_league"].includes(v)) return "league";
  return "neo";
}

function getStoredAppSelection() {
  return "neo";
}

function setLoginNavigationLocked(locked) {
  if (typeof window === "undefined") return;
  try {
    if (locked) window.sessionStorage.setItem(LOGIN_IN_PROGRESS_KEY, "1");
    else window.sessionStorage.removeItem(LOGIN_IN_PROGRESS_KEY);
  } catch (_) {}
}

function clearMorpheusLayerState() {
  if (typeof window === "undefined") return;
  try {
    [
      "mc-force-access-layer",
      "mc-access-layer-unlocked",
      "mc-current-profile",
      "mc-active-profile-current",
      "mc-user-context",
    ].forEach((key) => {
      window.sessionStorage.removeItem(key);
      window.localStorage.removeItem(key);
    });
  } catch (_) {}
}

function persistAppSelection(app) {
  const normalized = normalizeAppSelection(app);
  if (typeof window === "undefined") return normalized;
  try {
    window.sessionStorage.setItem("im.activeApp", normalized);
    window.localStorage.removeItem(APP_STORAGE_KEY);
    if (normalized !== "mc") clearMorpheusLayerState();
  } catch (_) {}
  return normalized;
}

function resolveReturnTo(locationState) {
  const from = locationState?.from;
  const pathname = typeof from === "string" ? from : from?.pathname;
  if (typeof pathname !== "string") return "";
  if (!pathname.startsWith("/")) return "";
  if (["/login", "/signup", "/ontime-login"].includes(pathname)) return "";
  return pathname + (from?.search || "") + (from?.hash || "");
}

function isReturnToAllowedForApp(returnTo, app) {
  const target = String(returnTo || "");
  const requested = normalizeAppSelection(app);
  if (!target.startsWith("/")) return "";
  if (requested === "mc") return target.startsWith("/morpheus-count") ? target : "";
  if (requested === "ontime") return target.startsWith("/ontime") ? target : "";
  if (requested === "league") return target.startsWith("/league") ? target : "";
  if (target.startsWith("/morpheus-count") || target.startsWith("/ontime") || target.startsWith("/ontime-login") || target.startsWith("/league")) return "";
  return target;
}

function orgHasApp(orgData, app) {
  const want = String(app || "").toLowerCase();
  if (!want) return false;

  const enabledApps = Array.isArray(orgData?.enabledApps)
    ? orgData.enabledApps.map((x) => String(x).toLowerCase())
    : [];

  const appsMap = orgData?.apps && typeof orgData.apps === "object"
    ? Object.entries(orgData.apps)
        .filter(([, enabled]) => enabled === true)
        .map(([key]) => String(key).toLowerCase())
    : [];

  return enabledApps.includes(want) || appsMap.includes(want) || String(orgData?.defaultApp || "").toLowerCase() === want;
}


function emailDocId(email) {
  return String(email || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "") || "member";
}

function normalizeMemberDocForLogin(docId, data = {}) {
  const email = String(data.email || data.emailLower || "").trim().toLowerCase();
  const apps = normalizeApps(data.apps || data.enabledApps || data.appAccess || data.appsMap || data);
  return {
    ...data,
    email,
    uid: data.uid || data.authUid || data.userId || (docId && docId !== emailDocId(email) ? docId : ""),
    authUid: data.authUid || data.uid || data.userId || (docId && docId !== emailDocId(email) ? docId : ""),
    apps,
  };
}


function normalizeExplicitMemberAppsForLogin(memberDoc = null) {
  if (!memberDoc || typeof memberDoc !== "object") return [];
  const values = [];
  const collect = (raw) => {
    normalizeApps(raw).forEach((app) => {
      const normalized = normalizeAppSelection(app);
      if (normalized) values.push(normalized);
    });
  };

  // Only fields edited by Super Admin tabs are trusted for user layer access.
  // Do not read defaultApp/role/owner from a member object here.
  collect(memberDoc.apps);
  collect(memberDoc.enabledApps);
  collect(memberDoc.appAccess);
  collect(memberDoc.appsMap);
  collect(memberDoc.access);
  collect(memberDoc.permissions);

  return [...new Set(values.filter(Boolean))];
}

function chooseBestMemberDoc(docs = [], uid = "") {
  const cleanUid = String(uid || "").trim();
  if (!docs.length) return null;
  return docs
    .map((entry) => ({
      ...entry,
      weight:
        cleanUid && entry.id === cleanUid ? 0 :
        cleanUid && (entry.data?.uid === cleanUid || entry.data?.authUid === cleanUid || entry.data?.userId === cleanUid) ? 1 :
        entry.data?.isEmailMirror === true || entry.data?.mirrorOf ? 3 :
        2,
    }))
    .sort((a, b) => a.weight - b.weight)[0];
}
async function resolveOrgDocForUser(claims, email, uid = "") {
  const normalizedEmail = String(email || "").trim().toLowerCase();
  const cleanUid = String(uid || "").trim();
  const claimOrgId = claims?.orgId ? String(claims.orgId) : "";

  if (claimOrgId) {
    const orgSnap = await getDoc(doc(db, "orgs", claimOrgId));
    let member = null;
    if (cleanUid) {
      const uidSnap = await getDoc(doc(db, "orgs", claimOrgId, "members", cleanUid));
      if (uidSnap.exists()) member = normalizeMemberDocForLogin(uidSnap.id, uidSnap.data() || {});
    }
    if (!member && normalizedEmail) {
      const emailSnap = await getDoc(doc(db, "orgs", claimOrgId, "members", emailDocId(normalizedEmail)));
      if (emailSnap.exists()) member = normalizeMemberDocForLogin(emailSnap.id, emailSnap.data() || {});
    }
    if (orgSnap.exists()) return { orgId: orgSnap.id, data: orgSnap.data() || {}, member };
  }

  if (normalizedEmail) {
    try {
      const candidateDocs = [];
      for (const field of ["email", "emailLower"]) {
        try {
          const memberSnap = await getDocs(query(collectionGroup(db, "members"), where(field, "==", normalizedEmail), limit(25)));
          memberSnap.docs.forEach((d) => candidateDocs.push(d));
        } catch (_) {}
      }
      const seen = new Set();
      const candidates = candidateDocs.map((d) => {
        const key = d.ref.path;
        if (seen.has(key)) return null;
        seen.add(key);
        return {
          id: d.id,
          orgId: d.ref.parent.parent?.id || "",
          data: normalizeMemberDocForLogin(d.id, d.data() || {}),
        };
      }).filter((x) => x && x.orgId);
      const chosen = chooseBestMemberDoc(candidates, cleanUid);
      if (chosen?.orgId) {
        const orgSnap = await getDoc(doc(db, "orgs", chosen.orgId));
        return {
          orgId: chosen.orgId,
          data: orgSnap.exists() ? (orgSnap.data() || {}) : {},
          member: chosen.data || {},
        };
      }
    } catch (_) {}
  }

  if (!normalizedEmail) return null;

  const ownerQ = query(collection(db, "orgs"), where("ownerEmail", "==", normalizedEmail), limit(1));
  const ownerSnap = await getDocs(ownerQ);
  if (!ownerSnap.empty) {
    const first = ownerSnap.docs[0];
    return { orgId: first.id, data: first.data() || {} };
  }

  return null;
}

function mergeAuthContext(baseClaims = {}, roleDoc = null, orgData = null, user = null, memberDoc = null) {
  const merged = { ...(baseClaims || {}), ...(roleDoc || {}), ...(memberDoc || {}) };
  const baseApps = normalizeApps(baseClaims || {}).map(normalizeAppSelection).filter(Boolean);
  const roleApps = normalizeApps(roleDoc || {}).map(normalizeAppSelection).filter(Boolean);
  const explicitMemberApps = normalizeExplicitMemberAppsForLogin(memberDoc);
  const memberExists = !!memberDoc && typeof memberDoc === "object";

  // Login must use the same source of truth as the route guards: Super Admin
  // member toggles. This prevents stale custom claims/userRoles from letting an
  // MC-only username enter Neo directly from /login.
  const trustedApps = explicitMemberApps.length
    ? explicitMemberApps
    : [
        ...baseApps,
        ...roleApps,
        ...(memberExists ? [] : [normalizeAppSelection(baseClaims?.defaultApp), normalizeAppSelection(roleDoc?.defaultApp)]),
      ];

  merged.apps = [...new Set(trustedApps.filter(Boolean))];

  merged.defaultApp = merged.apps[0] || "";
  if (!merged.orgId && orgData?.orgId) merged.orgId = orgData.orgId;
  if (!merged.orgId && roleDoc?.orgId) merged.orgId = roleDoc.orgId;
  if (!merged.email && user?.email) merged.email = String(user.email).trim().toLowerCase();
  if (!merged.uid && user?.uid) merged.uid = String(user.uid).trim();
  return merged;
}

function hasNeoAccess(claims = {}, orgData = null) {
  const apps = Array.isArray(claims?.apps) ? claims.apps.map((x) => String(x).toLowerCase()) : [];
  const isSuper = claims?.superAdmin === true || claims?.superadmin === true;
  const neoAliases = ["cashup", "neo", "neo_admin", "cash-up"];
  if (isSuper) return true;
  // Access is per user from Super Admin member apps. Do not use org defaultApp.
  return apps.some((app) => neoAliases.includes(app));
}

function hasMorpheusAccess(claims = {}, orgData = null) {
  const apps = Array.isArray(claims?.apps) ? claims.apps.map((x) => String(x).toLowerCase()) : [];
  const isSuper = claims?.superAdmin === true || claims?.superadmin === true;
  const morpheusAliases = ["mc", "morpheus", "morpheus-count", "morphius"];
  if (isSuper) return true;
  // Access is per user from Super Admin member apps. Do not use org defaultApp.
  return apps.some((app) => morpheusAliases.includes(app));
}

async function resolveRoleDoc(uid) {
  if (!uid) return null;
  try {
    const snap = await getDoc(doc(db, "userRoles", String(uid)));
    if (snap.exists()) return snap.data() || {};
  } catch (_) {}
  return null;
}

function getOrgPreferredLanding(orgData, claims, activeApp = "neo") {
  const defaultApp = String(orgData?.defaultApp || "").toLowerCase();
  const role = String(claims?.onTimeRole || claims?.role || "").toLowerCase();
  const requested = normalizeAppSelection(activeApp);

  if (requested === "mc") { try { window.sessionStorage.setItem("mc-force-access-layer", "1"); } catch (_) {} return "/morpheus-count/workspace"; }

  if (defaultApp === "ontime") {
    if (role.includes("store")) return "/ontime/store";
    if (role.includes("driver")) return "/ontime/driver";
    return "/ontime";
  }

  if (defaultApp === "cashup") return "/cashup";
  if (["dashboard", "invoice", "neo"].includes(defaultApp)) return "/dashboard";
  return "";
}

export default function LoginPage({ preferredApp = "" }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [usernameOptions, setUsernameOptions] = useState([]);
  const [showUsernameOptions, setShowUsernameOptions] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [showReset, setShowReset] = useState(false);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [splash, setSplash] = useState({ app: "neo", phase: "Loading MatrixOS entry" });
  const splashTicketRef = useRef(0);
  const emailInputRef = useRef(null);
  const passwordInputRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const entryPreference = useMemo(() => normalizeAppSelection(
    preferredApp ||
      location.state?.preferredApp ||
      (location.pathname === "/ontime-login" ? "ontime" : getStoredAppSelection())
  ), [preferredApp, location.pathname, location.state]);
  const [selectedApp, setSelectedApp] = useState(entryPreference);
  const requestedApp = selectedApp;
  const isOnTimeEntry = requestedApp === "ontime";
  const isMorpheusEntry = requestedApp === "mc";
  const isLeagueEntry = requestedApp === "league";

  useEffect(() => {
    setSelectedApp(entryPreference);
  }, [entryPreference]);

  useEffect(() => {
    try {
      const saved = JSON.parse(window.localStorage.getItem(USERNAME_OPTIONS_KEY) || "[]");
      if (Array.isArray(saved)) {
        setUsernameOptions(saved.map((x) => String(x || "").trim()).filter(Boolean).slice(0, 8));
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    persistAppSelection(selectedApp);
  }, [selectedApp]);

  useEffect(() => {
    const ticket = splashTicketRef.current + 1;
    splashTicketRef.current = ticket;
    const sequence = ["neo", "mc", "ontime", "league"];
    setSplash({ app: "neo", phase: "Loading MatrixOS entry" });

    const timers = sequence.map((app, index) =>
      window.setTimeout(() => {
        if (splashTicketRef.current === ticket) {
          setSplash({ app, phase: index === 0 ? "Loading MatrixOS entry" : "Calibrating layer" });
        }
      }, index * 1000)
    );

    const finishTimer = window.setTimeout(() => {
      if (splashTicketRef.current === ticket) setSplash(null);
    }, 4200);

    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
      window.clearTimeout(finishTimer);
    };
  }, []);

  function rememberUserEmail(value) {
    setEmail(value || "");
  }

  function saveUsernameOption(value) {
    const clean = String(value || "").trim();
    if (!clean) return;
    setUsernameOptions((current) => {
      const next = [clean, ...current.filter((item) => item.toLowerCase() !== clean.toLowerCase())].slice(0, 8);
      try { window.localStorage.setItem(USERNAME_OPTIONS_KEY, JSON.stringify(next)); } catch (_) {}
      return next;
    });
  }

  function selectUsernameOption(value) {
    const clean = String(value || "").trim();
    if (!clean) return;
    if (emailInputRef.current) emailInputRef.current.value = clean;
    rememberUserEmail(clean);
    setShowUsernameOptions(false);
    window.setTimeout(() => passwordInputRef.current?.focus?.(), 40);
  }

  function rememberUserPassword(value) {
    setPassword(value || "");
  }

  function chooseApp(app) {
    const nextApp = persistAppSelection(app);
    setSelectedApp(nextApp);
    setMessage("");
  }

  function getLayerLabel(app = requestedApp) {
    if (app === "ontime") return "Link";
    if (app === "mc") return "Morpheus";
    if (app === "league") return "League";
    return "Neo";
  }

  function getLayerSub(app = requestedApp) {
    if (app === "ontime") return "DISPATCH LAYER";
    if (app === "mc") return "COUNT INTELLIGENCE";
    if (app === "league") return "PERFORMANCE LEAGUE";
    return "OPERATIONS ENGINE";
  }

  function showSplash(app = requestedApp, phase = "Opening layer") {
    splashTicketRef.current += 1;
    setSplash({ app, phase });
  }

  function showAccessDenied(app = requestedApp, text = "This account does not have access to this layer.") {
    splashTicketRef.current += 1;
    setSplash({
      app,
      phase: "Access blocked",
      error: text,
    });
    setMessage(text);
  }

  function closeAccessDenied() {
    setSplash(null);
    setLoading(false);
    setLoginNavigationLocked(false);
  }

  function contactAdminFromSplash() {
    try { setLoginNavigationLocked(false); } catch (_) {}
    setSplash(null);
    navigate("/contact", { replace: false });
  }

  function transitionTo(target, app = requestedApp) {
    const safeTarget = target || "/app-setup";
    showSplash(app, "Opening layer");
    return new Promise((resolve) => {
      window.setTimeout(() => {
        navigate(safeTarget, { replace: true });
        resolve();
      }, 1800);
    });
  }

  function goHome(e) {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    try { clearOnTimeSession(); } catch (_) {}
    try { window.sessionStorage.removeItem("matrixos.link.entryMode"); } catch (_) {}
    try { window.localStorage.removeItem("matrixos.link.entryMode"); } catch (_) {}
    try { window.sessionStorage.setItem("matrixos.forcePublicHome", "1"); } catch (_) {}

    const go = () => {
      if (typeof window !== "undefined") {
        window.history.replaceState(null, "", "/");
        window.location.replace(`${window.location.origin}/?home=1`);
        return;
      }
      navigate("/", { replace: true });
    };

    // Older Link PWA builds registered a root-scoped service worker. Unregister it before
    // leaving so the public homepage is not captured back into /link-store?source=pwa.
    try {
      if (typeof navigator !== "undefined" && navigator.serviceWorker?.getRegistrations) {
        navigator.serviceWorker
          .getRegistrations()
          .then((registrations) => Promise.all(
            registrations
              .filter((registration) => String(registration?.active?.scriptURL || registration?.waiting?.scriptURL || registration?.installing?.scriptURL || "").includes("link-site-sw"))
              .map((registration) => registration.unregister().catch(() => false))
          ))
          .finally(go);
        return;
      }
    } catch (_) {}

    go();
  }

  async function signInWithStorageRepair(auth, loginEmail, loginPassword) {
    try {
      await ensureSessionPersistence();
      return await signInWithEmailAndPassword(auth, loginEmail, loginPassword);
    } catch (err) {
      if (!isFirebaseAuthStorageError(err)) throw err;

      console.warn("[auth] Browser auth cache blocked login. Repairing and retrying once.", err?.message || err);
      await repairFirebaseAuthStorage();
      await ensureSessionPersistence();
      return await signInWithEmailAndPassword(auth, loginEmail, loginPassword);
    }
  }

  async function handleLogin(e) {
    e.preventDefault();
    const loginEmail = (emailInputRef.current?.value || email || "").trim();
    const loginPassword = passwordInputRef.current?.value || password || "";
    saveUsernameOption(loginEmail);
    setMessage("");
    setLoading(true);
    setLoginNavigationLocked(true);
    showSplash(requestedApp, "Verifying access");
    const auth = getAuthClient();

    try {
      persistAppSelection(requestedApp);
      // Clear the previous layer session before the new Firebase sign-in finishes.
      // This prevents the old layer (Neo/Morpheus/Link/Super) from flashing while
      // AuthProvider and route guards are rebuilding claims for the selected layer.
      if (requestedApp !== "ontime") {
        try { clearOnTimeSession(); } catch (_) {}
      }
      if (requestedApp !== "mc") clearMorpheusLayerState();

      await ensureSessionPersistence();

      if (auth.currentUser) {
        try {
          await signOut(auth);
        } catch (_) {}
      }

      const { user } = await signInWithStorageRepair(auth, loginEmail, loginPassword);
      await user.getIdToken(true);
      const tokenRes = await getIdTokenResult(user, true);
      const baseClaims = tokenRes?.claims || {};
      const roleDoc = await resolveRoleDoc(user.uid);
      const orgRef = await resolveOrgDocForUser({ ...baseClaims, ...(roleDoc || {}) }, loginEmail, user.uid);
      const orgData = orgRef?.data ? { ...orgRef.data, orgId: orgRef.orgId } : null;
      if (orgRef?.orgId) {
        try {
          window.sessionStorage.setItem("im.activeOrgId", String(orgRef.orgId));
          window.localStorage.setItem("im.activeOrgId", String(orgRef.orgId));
        } catch (_) {}
      }
      const mergedClaims = mergeAuthContext(baseClaims, roleDoc, orgData, user, orgRef?.member || null);
      const rawReturnTo = resolveReturnTo(location.state);
      const returnTo = isReturnToAllowedForApp(rawReturnTo, requestedApp);

      if (requestedApp === "ontime") {
        if (!hasOnTimeAccess(mergedClaims)) {
          await signOut(auth);
          showAccessDenied("ontime", "This account does not have access to Link. Ask an admin to activate Link for this username.");
          return;
        }
        await transitionTo(returnTo || "/ontime", "ontime");
        return;
      }

      if (requestedApp === "mc") {
        if (!hasMorpheusAccess(mergedClaims, orgData)) {
          await signOut(auth);
          showAccessDenied("mc", "This account does not have access to Morpheus Count. Ask an admin to activate Morpheus for this username.");
          return;
        }
        persistAppSelection("mc");
        try { window.sessionStorage.setItem("mc-force-access-layer", "1"); } catch (_) {}
        await transitionTo(returnTo || "/morpheus-count/workspace", "mc");
        return;
      }

      if (requestedApp === "league") {
        if (!hasLeagueAccess(mergedClaims)) {
          await signOut(auth);
          showAccessDenied("league", "This account does not have access to Matrix League. Ask an admin to activate League for this username.");
          return;
        }
        persistAppSelection("league");
        await transitionTo(returnTo || "/league", "league");
        return;
      }

      const isSuperUser = mergedClaims?.superAdmin === true || mergedClaims?.superadmin === true;
      if (!isSuperUser && !hasNeoAccess(mergedClaims, orgData)) {
        await signOut(auth);
        showAccessDenied("neo", "This account does not have access to Neo. Choose the correct layer or ask an admin to activate Neo for this username.");
        return;
      }

      persistAppSelection("neo");
      let target = isSuperUser ? "/super" : (returnTo || getOrgPreferredLanding(orgData, mergedClaims, requestedApp) || getLandingPath(mergedClaims, { activeAppHint: "neo" }));
      if (String(target || "").startsWith("/ontime")) {
        await signOut(auth);
        showAccessDenied("neo", "This account does not have access to Neo. Choose the correct layer or ask an admin to activate Neo for this username.");
        return;
      }

      await transitionTo(target || "/app-setup", "neo");
    } catch (err) {
      console.error("signIn error:", err);
      setSplash(null);
      if (isFirebaseAuthStorageError(err)) {
        await repairFirebaseAuthStorage();
        setMessage("❌ Login cache was repaired. Please press Enter again.");
      } else {
        setMessage("❌ Email or password is incorrect.");
      }
    } finally {
      setLoginNavigationLocked(false);
      setLoading(false);
    }
  }

  async function handleReset(e) {
    e.preventDefault();
    setMessage("");
    try {
      await sendPasswordResetEmail(getAuthClient(), resetEmail.trim());
      setMessage("✅ Password reset email sent!");
      setShowReset(false);
      setResetEmail("");
    } catch (err) {
      console.error("reset error:", err);
      setMessage(`❌ ${err.message}`);
    }
  }

  const layerMeta = {
    neo: {
      eyebrow: "neo // operations engine",
      navLabel: "Neo",
      navDesc: "Live Operations",
      title: "Enter the operations engine.",
      intro: "Live workflows, clock-ins, duties, incidents, and daily execution inside the MatrixOS command layer.",
      accentText: "text-sky-300",
      color: "#38bdf8",
      dot: "#3b82f6",
      glow: "rgba(56,189,248,.38)",
      gradient: "from-cyan-300 via-sky-400 to-blue-500",
      bg: "radial-gradient(circle at 12% 20%, rgba(34,211,238,.16), transparent 34%), radial-gradient(circle at 88% 10%, rgba(59,130,246,.20), transparent 36%), linear-gradient(135deg, #020712 0%, #06192c 48%, #02060e 100%)",
    },
    mc: {
      eyebrow: "morpheus // count intelligence",
      navLabel: "Morpheus",
      navDesc: "Count Intelligence",
      title: "Enter the stock intelligence layer.",
      intro: "Stock sessions, imports, archives, variance, and billing signals translated into clear count intelligence.",
      accentText: "text-emerald-300",
      color: "#34d399",
      dot: "#22c55e",
      glow: "rgba(52,211,153,.36)",
      gradient: "from-emerald-300 via-green-400 to-teal-400",
      bg: "radial-gradient(circle at 12% 20%, rgba(52,211,153,.18), transparent 34%), radial-gradient(circle at 88% 14%, rgba(34,197,94,.20), transparent 36%), linear-gradient(135deg, #020b08 0%, #06251b 52%, #020906 100%)",
    },
    league: {
      eyebrow: "league // performance competition",
      navLabel: "League",
      navDesc: "Performance League",
      title: "Enter the arena.",
      intro: "Turn operational excellence into a season your teams want to win.",
      accentText: "text-amber-300",
      color: "#f5c451",
      dot: "#f5c451",
      glow: "rgba(245,196,81,.36)",
      gradient: "from-amber-200 via-yellow-400 to-amber-500",
      bg: "radial-gradient(circle at 12% 18%, rgba(245,196,81,.18), transparent 34%), radial-gradient(circle at 88% 16%, rgba(217,164,35,.18), transparent 36%), linear-gradient(135deg, #080704 0%, #17130a 50%, #050504 100%)",
    },
    ontime: {
      eyebrow: "link // dispatch layer",
      navLabel: "Link",
      navDesc: "Dispatch Layer",
      title: "Enter the dispatch engine.",
      intro: "Live workflows, store queues, driver runs, and real-time movement.",
      accentText: "text-orange-300",
      color: "#fb923c",
      dot: "#f59e0b",
      glow: "rgba(251,146,60,.36)",
      gradient: "from-orange-300 via-amber-400 to-orange-500",
      bg: "radial-gradient(circle at 11% 18%, rgba(251,146,60,.16), transparent 34%), radial-gradient(circle at 88% 20%, rgba(245,158,11,.20), transparent 36%), linear-gradient(135deg, #120701 0%, #2a1407 50%, #120804 100%)",
    },
  };
  const activeLayer = layerMeta[requestedApp] || layerMeta.neo;
  const splashLayer = layerMeta[splash?.app || requestedApp] || layerMeta.neo;

  const layerCards = [
    { id: "neo", name: "Neo", kicker: "Live Operations", body: "Clock-ins, duties, incidents, teams, and daily command." },
    { id: "mc", name: "Morpheus", kicker: "Count Intelligence", body: "Stock sessions, imports, archives, variance, and billing signals." },
    { id: "ontime", name: "Link", kicker: "Dispatch Layer", body: "Online intake, store queues, driver runs, and live movement." },
    { id: "league", name: "League", kicker: "Performance League", body: "Standings, seasons, fixtures, form, and operational competition." },
  ];

  function LayerWordLogo({ id, size = "card", active = false }) {
    const meta = layerMeta[id] || layerMeta.neo;
    const label = id === "ontime" ? "Link" : id === "mc" ? "Morpheus" : id === "league" ? "League" : "Neo";
    const isCard = size === "card";
    const isBrand = size === "brand";
    const ringSize = isCard ? "h-20 w-20" : isBrand ? "h-24 w-24" : "h-64 w-64";
    const textSize = isCard
      ? (id === "mc" ? "text-[0.82rem]" : "text-[1.15rem]")
      : isBrand
        ? (id === "mc" ? "text-[0.95rem]" : "text-[1.35rem]")
        : (id === "mc" ? "text-[2.35rem]" : "text-[3.3rem]");

    return (
      <div
        className={`matrixos-coded-logo ${ringSize} relative grid shrink-0 place-items-center rounded-full border transition`}
        style={{
          "--matrixos-logo-accent": meta.color,
          borderColor: active || !isCard ? meta.color : "rgba(255,255,255,.20)",
          background: `radial-gradient(circle at 50% 48%, ${meta.glow}, rgba(2,6,23,.48) 42%, rgba(2,6,23,.18) 72%)`,
          boxShadow: `${active || !isCard ? `0 0 18px ${meta.color}, 0 0 42px ${meta.glow}, inset 0 0 34px ${meta.glow}` : `inset 0 0 24px rgba(255,255,255,.04)`}`,
        }}
      >
        <span
          className={`matrixos-coded-word ${textSize} font-black italic leading-none tracking-[-0.08em] text-white`}
          style={{ textShadow: "0 0 18px rgba(255,255,255,.34), 0 2px 0 rgba(255,255,255,.08)" }}
        >
          {label}<span style={{ color: meta.dot, textShadow: `0 0 18px ${meta.dot}` }}>•</span>
        </span>
      </div>
    );
  }

  function LayerIcon(props) {
    return <LayerWordLogo {...props} />;
  }

  function BrandMark() {
    const label = requestedApp === "ontime" ? "Link" : requestedApp === "mc" ? "Morpheus" : requestedApp === "league" ? "League" : "Neo";
    const sub = requestedApp === "ontime" ? "DISPATCH LAYER" : requestedApp === "mc" ? "COUNT INTELLIGENCE" : requestedApp === "league" ? "PERFORMANCE LEAGUE" : "OPERATIONS ENGINE";
    return (
      <div className="matrixos-brand absolute left-8 top-8 z-20 select-none">
        <div className="matrixos-brand-title text-5xl font-bold italic leading-none tracking-[-0.08em] text-white drop-shadow-[0_0_24px_rgba(255,255,255,.28)]">
          {label}<span style={{ color: activeLayer.dot, textShadow: `0 0 18px ${activeLayer.dot}` }}>•</span>
        </div>
        <div className="matrixos-brand-sub mt-2 text-xs font-bold uppercase tracking-[0.34em] text-cyan-300">{sub}</div>
        <div className="matrixos-brand-actions mt-4 flex flex-col items-start gap-2">
          <button type="button" onClick={goHome} className="rounded-full border border-white/15 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-white/80 transition hover:text-white" style={{ background: `linear-gradient(135deg, rgba(255,255,255,.06), ${activeLayer.glow})`, boxShadow: "0 0 0 rgba(0,0,0,0)" }} onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 0 28px ${activeLayer.glow}`; e.currentTarget.style.borderColor = activeLayer.color; }} onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 0 0 rgba(0,0,0,0)"; e.currentTarget.style.borderColor = "rgba(255,255,255,.15)"; }}>Home</button>
          <Link to="/scouts/login" className="rounded-full border border-white/15 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-white/80 transition hover:text-white" style={{ background: `linear-gradient(135deg, rgba(255,255,255,.06), ${activeLayer.glow})`, boxShadow: "0 0 0 rgba(0,0,0,0)" }} onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 0 28px ${activeLayer.glow}`; e.currentTarget.style.borderColor = activeLayer.color; }} onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 0 0 rgba(0,0,0,0)"; e.currentTarget.style.borderColor = "rgba(255,255,255,.15)"; }}>Scouts</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="matrixos-login-page relative min-h-screen overflow-x-hidden text-white" style={{ background: activeLayer.bg }}>
      <style>{`
        .matrixos-login-input,
        .matrixos-login-input:focus {
          background-color: rgba(2, 6, 23, 0.78) !important;
          color: #f8fafc !important;
          -webkit-text-fill-color: #f8fafc !important;
          caret-color: #67e8f9;
        }
        .matrixos-login-input:-webkit-autofill,
        .matrixos-login-input:-webkit-autofill:hover,
        .matrixos-login-input:-webkit-autofill:focus,
        .matrixos-login-input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 1000px rgba(2, 6, 23, 0.92) inset !important;
          box-shadow: 0 0 0 1000px rgba(2, 6, 23, 0.92) inset !important;
          -webkit-text-fill-color: #f8fafc !important;
          color: #f8fafc !important;
          transition: background-color 9999s ease-in-out 0s;
        }

        .matrixos-coded-logo::before {
          content: "";
          position: absolute;
          inset: -10px;
          border-radius: 9999px;
          background: radial-gradient(circle, currentColor 0%, transparent 66%);
          opacity: .12;
          filter: blur(18px);
          pointer-events: none;
        }
        .matrixos-coded-logo {
          color: var(--matrixos-logo-accent, #38bdf8);
        }
        .matrixos-coded-word {
          transform: translateX(-0.03em);
          white-space: nowrap;
        }
        .matrixos-splash-scan {
          animation: matrixosSplashScan 1.65s cubic-bezier(.2,.85,.25,1) forwards;
        }
        @keyframes matrixosSplashScan {
          0% { transform: translateX(-110%); opacity: .25; }
          35% { opacity: 1; }
          100% { transform: translateX(210%); opacity: .92; }
        }
        @keyframes matrixosSplashPop {
          0% { transform: translateY(12px) scale(.96); opacity: 0; filter: blur(6px); }
          100% { transform: translateY(0) scale(1); opacity: 1; filter: blur(0); }
        }
        .matrixos-login-page, .matrixos-login-page * { box-sizing: border-box; }
        .matrixos-login-page { width: 100%; max-width: 100vw; }
        .matrixos-login-shell { width: min(100%, 1340px); }
        .matrixos-login-grid { width: 100%; }
        .matrixos-brand-inner { width: max-content; max-width: calc(100vw - 2rem); }

        @media (max-width: 1700px) {
          .matrixos-login-page {
            min-height: 100vh !important;
            height: auto !important;
            overflow-x: hidden !important;
            overflow-y: auto !important;
          }
          .matrixos-brand {
            position: relative !important;
            left: auto !important;
            top: auto !important;
            padding: 1.25rem 1.25rem 0 !important;
          }
          .matrixos-brand-title {
            font-size: clamp(2.1rem, 5vw, 3.05rem) !important;
          }
          .matrixos-brand-sub {
            letter-spacing: .24em !important;
            font-size: .72rem !important;
          }
          .matrixos-brand-actions {
            flex-direction: row !important;
            flex-wrap: wrap !important;
          }
          .matrixos-login-shell {
            max-width: 1040px !important;
            min-height: auto !important;
            height: auto !important;
            align-items: stretch !important;
            padding: 1.25rem !important;
            padding-top: 1.25rem !important;
          }
          .matrixos-login-grid {
            display: grid !important;
            grid-template-columns: 1fr !important;
            gap: 1.25rem !important;
            width: 100% !important;
          }
          .matrixos-login-panel {
            width: 100% !important;
            max-width: 100% !important;
            padding: 2rem !important;
            border-radius: 1.75rem !important;
          }
          .matrixos-login-hero,
          .matrixos-login-title {
            font-size: clamp(2rem, 5.5vw, 3rem) !important;
            line-height: 1.02 !important;
          }
          .matrixos-login-intro,
          .matrixos-login-copy {
            font-size: clamp(.96rem, 2vw, 1.1rem) !important;
            line-height: 1.6 !important;
          }
          .matrixos-layer-card {
            padding: 1.1rem !important;
            gap: 1rem !important;
          }
          .matrixos-layer-icon {
            width: 4.25rem !important;
            height: 4.25rem !important;
            font-size: 2rem !important;
          }
        }


        @media (min-width: 1100px) and (max-width: 1700px) and (max-height: 900px) {
          .matrixos-login-shell {
            max-width: 1180px !important;
            padding-top: 1rem !important;
            padding-bottom: 2.5rem !important;
          }
          .matrixos-login-panel {
            padding: 1.65rem 2rem !important;
          }
          .matrixos-login-card-list {
            gap: .85rem !important;
          }
          .matrixos-layer-card {
            min-height: auto !important;
            padding: .95rem 1.15rem !important;
          }
          .matrixos-layer-icon {
            width: 3.8rem !important;
            height: 3.8rem !important;
            font-size: 1.85rem !important;
          }
          .matrixos-login-hero,
          .matrixos-login-title {
            font-size: clamp(2rem, 4.2vw, 2.65rem) !important;
          }
          .matrixos-login-intro,
          .matrixos-login-copy {
            font-size: .98rem !important;
            line-height: 1.48 !important;
          }
          .matrixos-login-input {
            padding-top: .95rem !important;
            padding-bottom: .95rem !important;
          }
        }

        @media (max-width: 720px) {
          .matrixos-brand {
            padding: 1rem 1rem 0 !important;
          }
          .matrixos-brand-title {
            font-size: 2.15rem !important;
          }
          .matrixos-brand-sub {
            letter-spacing: .18em !important;
            font-size: .66rem !important;
          }
          .matrixos-brand-actions a,
          .matrixos-brand-actions button {
            padding: .65rem .9rem !important;
            font-size: .68rem !important;
            letter-spacing: .12em !important;
          }
          .matrixos-login-shell {
            padding: 1rem !important;
          }
          .matrixos-login-panel {
            padding: 1.15rem !important;
            border-radius: 1.35rem !important;
          }
          .matrixos-login-hero,
          .matrixos-login-title {
            font-size: clamp(1.75rem, 10vw, 2.6rem) !important;
          }
          .matrixos-login-card-list {
            gap: .85rem !important;
          }
          .matrixos-layer-card {
            padding: .95rem !important;
            gap: .85rem !important;
            border-radius: 1.2rem !important;
          }
          .matrixos-layer-icon {
            width: 3.5rem !important;
            height: 3.5rem !important;
            font-size: 1.65rem !important;
          }
          .matrixos-layer-name {
            font-size: 1.15rem !important;
          }
          .matrixos-layer-body {
            font-size: .82rem !important;
            line-height: 1.35 !important;
          }
          .matrixos-login-input {
            padding-top: 1rem !important;
            padding-bottom: 1rem !important;
          }
        }
      `}</style>
      <div className="pointer-events-none absolute inset-0 opacity-70">
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(255,255,255,.035)_1px,transparent_1px),linear-gradient(180deg,rgba(255,255,255,.035)_1px,transparent_1px)] bg-[size:70px_70px]" />
        <div className="absolute -left-28 top-0 h-96 w-96 rounded-full blur-3xl" style={{ backgroundColor: activeLayer.glow }} />
        <div className="absolute right-0 top-0 h-[28rem] w-[28rem] rounded-full blur-3xl" style={{ backgroundColor: activeLayer.glow }} />
        <div className="absolute left-[9%] top-0 h-full w-px rotate-12 bg-cyan-200/14 shadow-[0_0_80px_rgba(34,211,238,.55)]" />
        <div className="absolute right-[8%] top-0 h-full w-px rotate-12 bg-cyan-200/12 shadow-[0_0_80px_rgba(34,211,238,.45)]" />
        <div className="absolute left-[13%] top-[48%] h-1.5 w-1.5 rounded-full" style={{ backgroundColor: activeLayer.color, boxShadow: `0 0 16px ${activeLayer.color}` }} />
        <div className="absolute right-[15%] top-[24%] h-1.5 w-1.5 rounded-full" style={{ backgroundColor: activeLayer.color, boxShadow: `0 0 16px ${activeLayer.color}` }} />
      </div>

      <BrandMark />

      {splash ? (
        <div className="fixed inset-0 z-50 grid place-items-center overflow-hidden text-white" style={{ background: splashLayer.bg }}>
          <div className="pointer-events-none absolute inset-0 opacity-80">
            <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(255,255,255,.04)_1px,transparent_1px),linear-gradient(180deg,rgba(255,255,255,.04)_1px,transparent_1px)] bg-[size:76px_76px]" />
            <div className="absolute left-1/2 top-1/2 h-[34rem] w-[34rem] -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl" style={{ backgroundColor: splashLayer.glow }} />
            <div className="absolute left-[28%] top-0 h-full w-px rotate-12 bg-cyan-200/16 shadow-[0_0_90px_rgba(34,211,238,.55)]" />
            <div className="absolute right-[28%] top-0 h-full w-px rotate-12 bg-cyan-200/12 shadow-[0_0_90px_rgba(34,211,238,.45)]" />
          </div>
          <div className="relative flex flex-col items-center text-center animate-[matrixosSplashPop_.45s_ease-out_both]">
            <LayerWordLogo id={splash.app} size="splash" active />
            <div className="mt-6 text-xs font-black uppercase tracking-[0.44em] text-cyan-200">{getLayerSub(splash.app)}</div>
            <div className="mt-10 h-1.5 w-72 overflow-hidden rounded-full bg-white/10">
              <div className={`h-full w-1/2 rounded-full bg-gradient-to-r ${splashLayer.gradient} shadow-[0_0_28px_rgba(34,211,238,.55)] matrixos-splash-scan`} />
            </div>
            <div className="mt-5 text-sm font-bold uppercase tracking-[0.28em] text-white/70">{splash.phase || "Opening layer"}</div>
            {splash.error ? (
              <div className="mt-7 w-[min(92vw,520px)] rounded-[1.6rem] border border-red-300/25 bg-red-500/10 p-5 shadow-[0_0_50px_rgba(248,113,113,.18)] backdrop-blur-xl">
                <div className="text-sm font-black uppercase tracking-[0.28em] text-red-200">No access</div>
                <p className="mt-3 text-base leading-7 text-white/85">{splash.error}</p>
                <div className="mt-5 flex flex-wrap justify-center gap-3">
                  <button
                    type="button"
                    onClick={closeAccessDenied}
                    className="rounded-full border border-white/15 bg-white/8 px-5 py-3 text-xs font-black uppercase tracking-[0.18em] text-white transition hover:bg-white/14"
                  >
                    Return
                  </button>
                  <button
                    type="button"
                    onClick={contactAdminFromSplash}
                    className={`rounded-full bg-gradient-to-r ${splashLayer.gradient} px-5 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-950 transition hover:scale-[1.02]`}
                  >
                    Contact admin
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="matrixos-login-shell relative z-10 mx-auto flex min-h-screen items-center px-8 py-8">
        <div className="matrixos-login-grid grid w-full items-center gap-8 min-[1701px]:grid-cols-[0.95fr_1.05fr]">
          <section className="matrixos-login-panel relative overflow-hidden rounded-[2rem] border border-white/18 bg-slate-950/54 p-10 shadow-[0_30px_100px_rgba(0,0,0,.50)] backdrop-blur-2xl">
            <div className={`pointer-events-none absolute -right-16 -top-16 h-52 w-52 rounded-full bg-gradient-to-br ${activeLayer.gradient} opacity-20 blur-3xl`} />
            <div className="text-xs uppercase tracking-[0.46em] text-cyan-200/90">choose your layer</div>
            <h1 className="matrixos-login-hero mt-7 max-w-xl text-[3.35rem] font-black leading-[0.96] tracking-[-0.06em] text-white drop-shadow-[0_3px_0_rgba(255,255,255,.08)]">
              Shift the system.<br />Access your world.
            </h1>
            <p className="matrixos-login-copy mt-6 max-w-xl text-base leading-7 text-slate-300">
              Neo, Morpheus, Link, and League each power a core part of MatrixOS. Select a layer to continue.
            </p>

            <div className="matrixos-login-card-list mt-8 grid gap-4">
              {layerCards.map((app) => {
                const meta = layerMeta[app.id] || layerMeta.neo;
                const active = requestedApp === app.id;
                return (
                  <button
                    key={app.id}
                    type="button"
                    onClick={() => chooseApp(app.id)}
                    className="matrixos-layer-card group relative flex items-center gap-5 rounded-[1.75rem] border p-5 text-left transition hover:-translate-y-0.5"
                    style={{
                      borderColor: active ? meta.color : "rgba(255,255,255,.10)",
                      background: active ? `linear-gradient(135deg, rgba(255,255,255,.09), ${meta.glow})` : "rgba(255,255,255,.035)",
                      boxShadow: active ? `0 0 44px ${meta.glow}` : "none",
                    }}
                  >
                    <LayerIcon id={app.id} active={active} />
                    <div className="min-w-0 flex-1">
                      <div className="matrixos-layer-name text-2xl font-black text-white">{app.name}</div>
                      <div className="mt-1 text-sm font-black" style={{ color: meta.color }}>{app.kicker}</div>
                      <div className="matrixos-layer-body mt-2 text-sm leading-5 text-slate-300">{app.body}</div>
                    </div>
                    <div className="text-5xl font-light text-white/85 transition group-hover:translate-x-1">›</div>
                    <span className="absolute right-6 top-6 h-3 w-3 rounded-full shadow-[0_0_16px_currentColor]" style={{ backgroundColor: meta.dot, color: meta.dot }} />
                  </button>
                );
              })}
            </div>
          </section>

          <section className="matrixos-login-panel relative overflow-hidden rounded-[2rem] border border-white/18 bg-slate-950/60 p-10 shadow-[0_30px_100px_rgba(0,0,0,.50)] backdrop-blur-2xl">
            <div className={`pointer-events-none absolute inset-x-6 top-0 h-1 rounded-full bg-gradient-to-r ${activeLayer.gradient}`} />
            <div className="text-xs uppercase tracking-[0.46em] text-cyan-200/90">{activeLayer.eyebrow}</div>
            <h2 className="matrixos-login-title mt-8 max-w-2xl text-[3.1rem] font-black leading-[0.98] tracking-[-0.06em] text-white drop-shadow-[0_3px_0_rgba(255,255,255,.08)]">{activeLayer.title}</h2>
            <p className="matrixos-login-intro mt-7 max-w-2xl text-xl leading-8 text-slate-300">{activeLayer.intro}</p>
            <div className="my-9 h-px bg-white/10" />

            <form onSubmit={handleLogin} className="space-y-6" autoComplete="off">
              <div>
                <label className="mb-3 block text-xs font-black uppercase tracking-[0.34em] text-cyan-200">Username</label>
                <div className="relative">
                  <span className="pointer-events-none absolute left-5 top-1/2 -translate-y-1/2 text-xl text-slate-400">✉</span>
                  <input
                    ref={emailInputRef}
                    type="search"
                    defaultValue=""
                    onChange={(e) => rememberUserEmail(e.currentTarget.value)}
                    onInput={(e) => rememberUserEmail(e.currentTarget.value)}
                    onFocus={() => setShowUsernameOptions(true)}
                    onBlur={() => window.setTimeout(() => setShowUsernameOptions(false), 160)}
                    required
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="none"
                    spellCheck={false}
                    name="matrixos-login-user"
                    id="matrixos-login-user"
                    className="matrixos-login-input w-full rounded-2xl border border-white/12 bg-slate-950/55 px-14 py-5 text-white outline-none transition placeholder:text-slate-500 focus:border-cyan-300/55 focus:ring-2 focus:ring-cyan-300/20"
                    style={{ background: "rgba(2, 6, 23, 0.78)", color: "#f8fafc", WebkitTextFillColor: "#f8fafc", boxShadow: "inset 0 0 0 1px rgba(255,255,255,.03)" }}
                  />
                  {showUsernameOptions && usernameOptions.length ? (
                    <div className="absolute left-0 right-0 top-[calc(100%+10px)] z-30 overflow-hidden rounded-2xl border border-white/12 bg-slate-950/95 p-2 shadow-[0_24px_70px_rgba(0,0,0,.55)] backdrop-blur-xl">
                      <div className="px-3 pb-2 pt-1 text-[0.68rem] font-black uppercase tracking-[0.22em] text-cyan-200/80">Saved usernames</div>
                      {usernameOptions.map((option) => (
                        <button
                          key={option}
                          type="button"
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => selectUsernameOption(option)}
                          className="block w-full rounded-xl px-3 py-3 text-left text-sm font-bold text-white/90 transition hover:bg-cyan-300/10 hover:text-cyan-100"
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="relative">
                <label className="mb-3 block text-xs font-black uppercase tracking-[0.34em] text-cyan-200">Password</label>
                <span className="pointer-events-none absolute left-5 top-[3.5rem] text-xl text-slate-400">▣</span>
                <input
                  ref={passwordInputRef}
                  type={showPassword ? "text" : "password"}
                  defaultValue=""
                  onChange={(e) => rememberUserPassword(e.currentTarget.value)}
                  onInput={(e) => rememberUserPassword(e.currentTarget.value)}
                  required
                  autoComplete="new-password"
                  name="matrixos-login-pass"
                  id="matrixos-login-pass"
                  className="matrixos-login-input w-full rounded-2xl border border-white/12 bg-slate-950/55 px-14 py-5 pr-20 text-white outline-none transition focus:border-cyan-300/55 focus:ring-2 focus:ring-cyan-300/20"
                  style={{ background: "rgba(2, 6, 23, 0.78)", color: "#f8fafc", WebkitTextFillColor: "#f8fafc", boxShadow: "inset 0 0 0 1px rgba(255,255,255,.03)" }}
                />
                <button type="button" onClick={() => setShowPassword((v) => !v)} className="absolute right-5 top-[3.55rem] text-sm font-bold text-cyan-200">
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
                <button type="button" onClick={() => setShowReset(true)} className="font-bold text-cyan-200 transition hover:text-white">
                  Forgot password?
                </button>
              </div>

              {message ? (
                <div className="rounded-2xl border border-cyan-300/18 bg-cyan-400/8 px-4 py-3 text-sm text-cyan-100">
                  {message}
                </div>
              ) : null}

              <button type="submit" disabled={loading} className={`w-full rounded-2xl bg-gradient-to-r ${activeLayer.gradient} px-6 py-5 text-base font-black text-slate-950 shadow-[0_0_40px_rgba(34,211,238,.22)] transition hover:scale-[1.01] disabled:opacity-60`}>
                {loading ? "Signing in..." : isMorpheusEntry ? "Enter Morpheus" : isOnTimeEntry ? "Enter Link" : isLeagueEntry ? "Enter League" : "Enter Neo"}
              </button>
            </form>

            {showReset ? (
              <form onSubmit={handleReset} className="mt-5 space-y-3 border-t border-white/10 pt-5">
                <div className="text-sm text-slate-300">Enter your username and we’ll send you a reset link.</div>
                <input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                  placeholder="name@example.com"
                  className="w-full rounded-2xl border border-white/12 bg-slate-950/70 px-4 py-4 text-white outline-none transition focus:border-cyan-300/55 focus:ring-2 focus:ring-cyan-300/20"
                />
                <div className="flex gap-3">
                  <button type="submit" className={`flex-1 rounded-full bg-gradient-to-r ${activeLayer.gradient} px-5 py-3 text-sm font-black text-slate-950`}>
                    Send reset
                  </button>
                  <button type="button" onClick={() => setShowReset(false)} className="rounded-full border border-white/12 px-5 py-3 text-sm font-semibold text-white transition hover:bg-white/5">
                    Cancel
                  </button>
                </div>
              </form>
            ) : null}
          </section>
        </div>
      </div>
    </div>
  );
}